import { Lts, TinypassSDK } from "@product/rosetta-sdk";
import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { graphql, readInlineData } from "react-relay";
import { useLatest } from "react-use";

import { config } from "shared/data";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { UntypedGA4Event } from "scmp-app/components/tracking/google-tag-manager/types";
import { currentArticleAtom } from "scmp-app/lib/current-item";
import { parseEnvironment, rosettaAtom, useRosettaContext } from "scmp-app/lib/rosetta";
import type { abandonedCartOfferDialogArticle$key } from "scmp-app/queries/__generated__/abandonedCartOfferDialogArticle.graphql";

export const AbandonedCartOfferDialog: FunctionComponent = () => {
  const { abandonedCartFlow } = useRosettaContext();
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const currentArticle = useAtomValue(currentArticleAtom);

  const latestDataReference = useLatest({ currentArticle });

  useEffect(() => {
    if (!abandonedCartFlow) return;

    const { currentArticle: latestArticle } = latestDataReference.current;
    const rosettaSDKInstance = asyncRosettaState?.result?.instance;
    const userCurrency = rosettaSDKInstance?.rulesEngine.getOutcome("user.currency");
    if (userCurrency === undefined || !asyncRosettaState?.result?.hasPianoExperienceExecuted)
      return;

    const currentArticleData = readInlineData<abandonedCartOfferDialogArticle$key>(
      graphql`
        fragment abandonedCartOfferDialogArticle on Article @inline {
          sections {
            value {
              name
            }
          }
          topics {
            name
          }
        }
      `,
      latestArticle,
    );

    const articleTopics =
      currentArticleData?.topics?.map(t => t?.name).filter((t): t is string => !!t) ?? [];

    const articleSections =
      currentArticleData?.sections
        ?.map(section =>
          section?.value
            ?.map(segment => segment?.name)
            .filter((name): name is string => !!name)
            .join("/"),
        )
        .filter((section): section is string => !!section) ?? [];

    try {
      void abandonedCartFlow.controller.showOfferDialog({
        config: {
          environment: parseEnvironment(config.rosetta.environment),
          scmpBaseURL: `https://${config.domain.scmp}`,
          subscriptionBaseURL: config.rosetta.subscribeHost,
          widgetBaseURL: config.rosetta.subscribeTemplateHost,
        },
        context: {
          app: {
            application: "pwa",
            tier: "standard",
          },
          article: {
            sections: articleSections,
            topics: articleTopics,
          },
          paywall: null,
          user: {
            currency: userCurrency,
          },
        },
        services: {
          eventBus: abandonedCartFlow.eventBus,
          tracker: {
            track(event) {
              const segments = event.name.split("_");

              sendGA4Tracking(
                {
                  action: segments.at(-1),
                  category: segments.at(0),
                  customized_parameters: {
                    ...rosettaSDKInstance?.rulesEngine.getOutcome("tracking.ga4.baseParameters"),
                    lts_bucket: Lts.getLtsBucket({
                      customVariables: TinypassSDK.loadedInstance?.customVariables,
                    }),
                    ...event.parameters,
                  },
                  subcategory: segments.slice(1, -1).join("_"),
                } as unknown as UntypedGA4Event,
                { untyped: true },
              );
            },
          },
        },
      });
    } catch (error) {
      console.error("Failed to show abandoned cart offer dialog", { cause: error });
    }
  }, [abandonedCartFlow, asyncRosettaState, latestDataReference]);

  return null;
};
